import { Component, OnDestroy, OnInit } from '@angular/core';
import { CustomDateSelectComponent } from '../../shared/components/custom-date-select/custom-date-select.component';
import { Subject, takeUntil } from 'rxjs';
import { BaseErrorComponent } from '../baseerror/baseerror.component';
import { FormsModule } from '@angular/forms';
import { GroupMember } from '../../Interface/Dealer';
import { GlobalService } from '../../core/services/global/global.service';
import { PrintRequestService } from '../../core/services/print-request/print-request.service';
import { ErrorPageComponent } from '../../shared/components/app-error-page/app-error-page.component';
import { PopupComponent } from '../../shared/components/popup/popup.component';
import { NadaSpinnerComponent } from '../../shared/components/nada-spinner/nada-spinner.component';
import { AuthenticationService } from '../../core/services/Auth/authservice';

@Component({
  selector: 'app-print-request',
  standalone: true,
  imports: [
    CustomDateSelectComponent,
    FormsModule,
    ErrorPageComponent,
    PopupComponent,
    NadaSpinnerComponent,
  ],
  templateUrl: './print-request.component.html',
  styleUrl: './print-request.component.css',
})
export class PrintRequestComponent
  extends BaseErrorComponent
  implements OnInit, OnDestroy
{
  private readonly unsubscribe$ = new Subject<void>();

  minDate = new Date(new Date().getFullYear() - 1, new Date().getMonth() - 1);
  maxDate = new Date(new Date().getFullYear(), new Date().getMonth());
  selectedDate: Date = this.maxDate;
  selectedCompDate: Date = new Date(
    new Date().getFullYear(),
    new Date().getMonth() - 1,
  );
  authUser: any = null;

  selectedDealer: GroupMember | null = null;
  ShowModal: boolean = false;
  showModalSpinner: boolean = false;
  showLoadingSpinner: boolean = false;

  tableData: any[] = [];
  isSavebuttonEnable: boolean = false;
  currentSelectedMetting: any = null;
  currentSelectedMeetingDetails: any = null;
  meetingDetailsErrorMessage: string = '';

  constructor(
    private globalService: GlobalService,
    private printRequestService: PrintRequestService,
    private authService: AuthenticationService,
  ) {
    super();
  }

  ngOnInit() {
    this.globalService.selectedDealer$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data) => {
        this.selectedDealer = data;
        this.getMeetingDetails();
      });
    this.authService.fincomData$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state) => {
        this.authUser = state;
      });
    this.getMeetingDetails();
  }

  onDateSelected(date: Date): void {
    this.selectedDate = date;
    this.getMeetingDetails();
  }

  onCompDateSelected(event: Date) {
    this.selectedCompDate = event;
    console.log(this.selectedCompDate);
  }

  async getMeetingDetails() {
    if (!this.selectedDate || !this.selectedDealer) return;
    this.resetErrorPage();
    this.showLoadingSpinner = true;
    this.tableData = [];
    const payload = {
      month: this.selectedDate.getMonth() + 1,
      year: this.selectedDate.getFullYear(),
      loggedInUserEmail: this.authUser.email,
      groupMemberCode: this.selectedDealer?.groupMemberCode,
    };
    try {
      const response =
        await this.printRequestService.getPrintRequestData(payload);
      if (response.statusCode !== 200) {
        this.setErrorPage(response.statusCode);
      } else if (response.data.print_requests_data.length === 0) {
        this.setErrorPage(204);
      }
      this.tableData = response.data.print_requests_data;
    } catch (error: any) {
      this.setErrorPage(error.statusCode || 500);
    } finally {
      this.showLoadingSpinner = false;
    }
  }

  getButtonText() {
    return this.showModalSpinner ? 'Please wait...' : 'Edit';
  }

  async onRequestPrint(item: any) {
    this.showModalSpinner = true;
    this.currentSelectedMetting = item;
    this.meetingDetailsErrorMessage = '';
    try {
      const resp = await this.printRequestService.getPrintRequestPopupData(
        item.Meeting_Id,
      );
      this.ShowModal = true;
      if (resp.statusCode === 200) {
        this.currentSelectedMeetingDetails = resp.data.print_request_popup_data;

        if (this.currentSelectedMeetingDetails.Month)
          this.selectedCompDate.setMonth(
            this.currentSelectedMeetingDetails.Month - 1,
          );
        else this.selectedCompDate.setMonth(this.selectedDate.getMonth() - 1);

        if (this.currentSelectedMeetingDetails.Month)
          this.selectedCompDate.setFullYear(
            this.currentSelectedMeetingDetails.Year,
          );
        else this.selectedCompDate.setFullYear(this.selectedDate.getFullYear());
      } else {
        this.currentSelectedMeetingDetails = null;
        this.meetingDetailsErrorMessage = 'Unable to fetch meeting details.';
      }
    } catch (error) {
      console.log('Unable to fetch meeting details', error);
      this.currentSelectedMeetingDetails = null;
      this.meetingDetailsErrorMessage = 'Unable to fetch meeting details.';
    } finally {
      this.showModalSpinner = false;
    }
  }

  async updatePrintRequest() {
    this.showModalSpinner = true;
    const payload = {
      Group_Code: this.currentSelectedMeetingDetails.Group_Code,
      Month: this.selectedCompDate.getMonth() + 1,
      Year: this.selectedCompDate.getFullYear(),
      Plus: this.currentSelectedMeetingDetails.Plus,
      Variable: this.currentSelectedMeetingDetails.Variable,
      DGM: this.currentSelectedMeetingDetails.DGM,
      Classic: this.currentSelectedMeetingDetails.Classic,
      Fixed: this.currentSelectedMeetingDetails.Fixed,
      Send_Plus_Book_To_Consultant_Home:
        this.currentSelectedMeetingDetails.Send_Plus_Book_To_Consultant_Home,
      Special_Instructions:
        this.currentSelectedMeetingDetails.Special_Instructions,
      Meeting_Id: this.currentSelectedMeetingDetails.Meeting_Id,
      Updated_By: this.authUser.email || 'unknown',
    };

    try {
      const response =
        await this.printRequestService.savePrintRequestPopupData(payload);
      if (response.statusCode == 200) {
        this.globalService.showToast(
          'Print request submitted successfully.',
          'success',
        );
        this.getMeetingDetails();
        this.onModalClose();
      } else {
        this.globalService.showToast(
          'Failed to save changes. Please try again.',
          'error',
        );
      }
    } catch (error: any) {
      console.error('Failed to save print request data', error);
      this.globalService.showToast(
        'Failed to save changes. Please try again.',
        'error',
      );
    } finally {
      this.showModalSpinner = false;
    }
  }

  onModalClose() {
    this.ShowModal = false;
    this.currentSelectedMetting = null;
    this.meetingDetailsErrorMessage = '';
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
